import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  getReferralsTopApi,
  TopReferrerItem,
} from "../../features/referrals/api/referrals.api"
import { TrophyIcon, ChevronRightIcon, ExclamationTriangleIcon } from "@heroicons/react/16/solid"

const LIMIT = 10

const RankBadge = ({ rank }: { rank: number }) => {
  const styles = [
    "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
    "bg-gray-100 text-gray-600 ring-1 ring-gray-200",
    "bg-orange-100 text-orange-700 ring-1 ring-orange-200",
  ]
  const style = styles[rank] ?? "bg-gray-50 text-gray-400 ring-1 ring-gray-100"
  return (
    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${style}`}>
      {rank + 1}
    </span>
  )
}

export const TopReferrersPage = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<TopReferrerItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getReferralsTopApi(LIMIT)
      .then((res) => setItems(res.data.items))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">Топ рефереров</h3>
        <span className="text-xs text-gray-400">{items.length} записей</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/40">
              {["#", "Пользователь", "Email", "Активаций", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {[1, 2, 3, 4, 5].map((j) => (
                    <td key={j} className="px-4 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center">
                  <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400 mb-2">Не удалось загрузить данные</p>
                  <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
                    Повторить
                  </button>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center">
                  <TrophyIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Пока нет данных</p>
                </td>
              </tr>
            ) : (
              items.map((item, i) => (
                <tr
                  key={item.user_id}
                  onClick={() => navigate(`/admin/referrals/users/${item.user_id}`)}
                  className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                >
                  <td className="px-4 py-3.5"><RankBadge rank={i} /></td>
                  <td className="px-4 py-3.5">
                    <p className="font-medium text-gray-800">{item.full_name || <span className="text-gray-300 italic">Без имени</span>}</p>
                    <p className="text-xs text-gray-400 font-mono">{item.user_id.slice(0, 12)}…</p>
                  </td>
                  <td className="px-4 py-3.5 text-gray-600">{item.email}</td>
                  <td className="px-4 py-3.5">
                    <span className="font-semibold text-emerald-600">{item.activated_count}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
